import { useState, useEffect } from 'react'
import { Eye, Code } from 'lucide-react'
import JvsProductHtmlPreview from '@/components/JvsProductHtmlPreview'

interface ProductPreviewProps {
  data: any
  localImages?: File[]
}

export default function ProductPreview({ data, localImages }: ProductPreviewProps) {
  const [viewMode, setViewMode] = useState<'rendered' | 'code'>('rendered')

  // Extraer HTML renderizado del resultado
  const descriptionLong =
    data.html ||  // Respuesta directa del servicio
    data.html_content ||
    data.extracted_data?.html ||
    data.preview?.description_long ||
    data.extracted_data?.description_long ||
    ''

  const productName =
    data.preview?.title ||
    data.preview?.name ||
    data.extracted_data?.name ||
    'Producto'

  // Prioritize local images if available
  const [previewImages, setPreviewImages] = useState<string[]>([])

  // Update images when data or localImages changes
  useEffect(() => {
    let urls: string[] = []

    if (localImages && localImages.length > 0) {
      // Create object URLs for local files
      urls = localImages.map(file => URL.createObjectURL(file))
      setPreviewImages(urls)
    } else {
      // User requested NOT to show scraped images.
      // "las imagenes no las debe obtener de la url"
      setPreviewImages([])
    }

    // Cleanup function to revoke URLs
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url))
    }
  }, [localImages, data])

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Vista Previa del Producto</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('rendered')}
            className={`px - 3 py - 1 rounded - lg text - sm font - medium transition - colors ${viewMode === 'rendered'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
              } `}
          >
            <Eye className="w-4 h-4 inline mr-1" />
            Renderizado
          </button>
          <button
            onClick={() => setViewMode('code')}
            className={`px - 3 py - 1 rounded - lg text - sm font - medium transition - colors ${viewMode === 'code'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
              } `}
          >
            <Code className="w-4 h-4 inline mr-1" />
            HTML
          </button>
        </div>
      </div>

      {viewMode === 'rendered' ? (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
          {/* Título del producto */}
          <h2 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
            {productName}
          </h2>

          {/* Imágenes si existen */}
          {previewImages.length > 0 && (
            <div className="mb-6 grid grid-cols-2 md:grid-cols-3 gap-2">
              {previewImages.map((img: string, idx: number) => (
                <img
                  key={idx}
                  src={img}
                  alt={`${productName} - imagen ${idx + 1} `}
                  className="w-full h-auto rounded border border-gray-300 dark:border-gray-600"
                />
              ))}
            </div>
          )}

          {/* Descripción renderizada con el CSS real de la tienda (fichas v2 sin estilos inline) */}
          <JvsProductHtmlPreview html={descriptionLong} maxHeight={900} title="Descripción del producto" />
        </div>
      ) : (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-900 overflow-auto">
          <pre className="text-xs text-gray-900 dark:text-gray-100">
            <code>{descriptionLong}</code>
          </pre>
        </div>
      )}
    </div>
  )
}
